/**
 * The Draft_Check list (route `/check-drafts`; Feature: ai-check-generation, task 23.4;
 * Requirements 9.7, 9.8; Design: §Components 11).
 *
 * `GET /v1/check-drafts` with the platform's `LoadState` shape and the `ChecksListPage` table
 * conventions. Four things are worth stating explicitly:
 *
 *  - **State and expiry are the point.** Req 9.8 asks for the Account's drafts *with their state and
 *    expiry timestamp*, so both are columns rather than detail discovered on the review page. Drafts
 *    of every state are listed, including `discarded` ones: a discarded draft is marked, not deleted,
 *    and stays listable until it ages out, which is what makes "why is there no Check from that
 *    generation?" answerable.
 *  - **A draft may have no name.** `configuration` is an untyped Check *payload* that can legitimately
 *    be `{}` — the correction-exhausted case — so the row label falls back to the `draftId` rather
 *    than assuming `logicalName` is present. Every row stays linkable either way.
 *  - **Discard only, and only while pending.** A Check is created solely through accept, which lives
 *    on the review page (Req 9.4), so this list offers no accept action. Discard is offered on
 *    `pending` rows only: the API answers a discard of an `accepted` draft with a `409` because that
 *    draft has already produced a Check, and a repeat discard is a no-op.
 *  - **A viewer sees the list, not the actions** (Req 9.7), via the existing `use-can-write` gate. As
 *    everywhere else in the dashboard this is display-only; the API is the enforcement point.
 *
 * The list re-fetches only after a confirmed discard — rows are never mutated locally, so a failed
 * discard leaves the pre-request view intact with the error in the dialog.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../api/client';
import type { CheckDraftDto, SourceDocumentRefDto } from '../api/types';
import { useApiClient } from '../api/use-api-client';
import { useCanWrite } from '../auth/use-can-write';
import { DraftStateBadge } from '../components/Badges';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { formatEpochSeconds } from '../lib/format';
import { useWrite } from '../lib/use-write';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string; isAuthError: boolean }
  | { status: 'loaded'; drafts: CheckDraftDto[] };

/**
 * A row label for a draft. `configuration` has passed only the pure Tier-A validation and may be
 * `{}`, so `logicalName` is read defensively and the `draftId` is the fallback — the row must stay
 * identifiable and linkable for a draft the model never managed to name.
 */
function draftLabel(draft: CheckDraftDto): string {
  const name = draft.configuration['logicalName'];
  return typeof name === 'string' && name.trim() !== '' ? name : draft.draftId;
}

/** The draft's Check type, when the generated payload got far enough to carry one. */
function draftType(draft: CheckDraftDto): string {
  const type = draft.configuration['type'];
  return typeof type === 'string' && type !== '' ? type : '—';
}

/** A one-line summary of where the draft came from — reference only, never content (Req 9.3). */
function describeSource(source: SourceDocumentRefDto): string {
  if (source.mode === 'url') return `URL: ${source.url}`;
  if (source.mode === 'pasted') return `Pasted text (${source.characterCount} chars)`;
  return `Upload: ${source.documentKey}`;
}

/** Outstanding validation errors and secret-substitution warnings, summarised for the row. */
function describeIssues(draft: CheckDraftDto): string {
  const errors = draft.validationErrors?.length ?? 0;
  const warnings = draft.warnings?.length ?? 0;
  const parts: string[] = [];
  if (errors > 0) parts.push(`${errors} error(s)`);
  if (warnings > 0) parts.push(`${warnings} warning(s)`);
  return parts.length > 0 ? parts.join(', ') : '—';
}

export function CheckDraftsListPage(): JSX.Element {
  const client = useApiClient();
  const canWrite = useCanWrite();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pendingDiscard, setPendingDiscard] = useState<CheckDraftDto | undefined>(undefined);
  const [discardNotice, setDiscardNotice] = useState<string | undefined>(undefined);
  const write = useWrite();

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    client
      .listCheckDrafts()
      .then((response) => {
        if (active) setState({ status: 'loaded', drafts: response.checkDrafts });
      })
      .catch((error: unknown) => {
        if (!active) return;
        const message = error instanceof Error ? error.message : 'Failed to load check drafts';
        const isAuthError = error instanceof ApiError && error.isAuthError;
        setState({ status: 'error', message, isAuthError });
      });
    return () => {
      active = false;
    };
  }, [client, reloadToken]);

  function confirmDiscard(draft: CheckDraftDto): void {
    void write.run(
      () => client.discardCheckDraft(draft.draftId),
      () => {
        setPendingDiscard(undefined);
        setDiscardNotice(`Discarded the draft ${draftLabel(draft)}.`);
        // Only a confirmed discard refreshes the list.
        setReloadToken((token) => token + 1);
      },
    );
  }

  return (
    <section>
      <div className="section-header">
        <h1>Check drafts</h1>
        {/* Generation is a write action, so a viewer is not offered it (Req 9.7). */}
        {canWrite ? <Link to="/checks/generate">Generate a check</Link> : null}
      </div>

      {discardNotice !== undefined ? (
        <p role="status" className="muted">
          {discardNotice}
        </p>
      ) : null}

      <CheckDraftsBody
        state={state}
        canWrite={canWrite}
        onDiscard={(draft) => {
          write.reset();
          setDiscardNotice(undefined);
          setPendingDiscard(draft);
        }}
      />

      {pendingDiscard !== undefined ? (
        <ConfirmDialog
          title="Discard check draft"
          message="Discarding this draft ends its review. No check is created, and the draft stays visible here until it expires."
          confirmLabel="Discard draft"
          busy={write.state.submitting}
          errorMessage={write.state.errorMessage}
          onConfirm={() => confirmDiscard(pendingDiscard)}
          onCancel={() => {
            write.reset();
            setPendingDiscard(undefined);
          }}
        />
      ) : null}
    </section>
  );
}

function CheckDraftsBody({
  state,
  canWrite,
  onDiscard,
}: {
  state: LoadState;
  canWrite: boolean;
  onDiscard: (draft: CheckDraftDto) => void;
}): JSX.Element {
  if (state.status === 'loading') {
    return (
      <p role="status" className="muted">
        Loading check drafts…
      </p>
    );
  }

  if (state.status === 'error') {
    return (
      <div role="alert" className="error-panel">
        <p>
          {state.isAuthError ? 'Your session expired. Please sign in again.' : 'Could not load check drafts.'}
        </p>
        <p className="muted">{state.message}</p>
      </div>
    );
  }

  if (state.drafts.length === 0) {
    return <p className="muted">No check drafts exist for this account yet.</p>;
  }

  return (
    <table className="checks-table" aria-label="Check drafts">
      <thead>
        <tr>
          <th scope="col">Draft</th>
          <th scope="col">Type</th>
          <th scope="col">Source</th>
          <th scope="col">State</th>
          <th scope="col">Expires</th>
          <th scope="col">Issues</th>
          {canWrite ? <th scope="col">Actions</th> : null}
        </tr>
      </thead>
      <tbody>
        {state.drafts.map((draft) => (
          <tr key={draft.draftId}>
            <td>
              <Link to={`/check-drafts/${encodeURIComponent(draft.draftId)}`}>{draftLabel(draft)}</Link>
            </td>
            <td>{draftType(draft)}</td>
            <td>{describeSource(draft.sourceDocument)}</td>
            <td>
              <DraftStateBadge state={draft.state} />
            </td>
            <td>{formatEpochSeconds(draft.expiresAtEpoch)}</td>
            <td>{describeIssues(draft)}</td>
            {canWrite ? (
              <td className="row-actions">
                {/* Accept lives on the review page: a Check is created only there (Req 9.4). A
                    non-pending draft has nothing left to discard. */}
                {draft.state === 'pending' ? (
                  <button
                    type="button"
                    className="danger"
                    aria-label={`Discard check draft ${draft.draftId}`}
                    onClick={() => onDiscard(draft)}
                  >
                    Discard
                  </button>
                ) : (
                  <span className="muted">—</span>
                )}
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
